import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { debounceTime, fromEvent, startWith } from 'rxjs';
import { DETAIL_EXPANDED } from '../../animations/detail-expanded';
import { UiAutoSizeDirective } from '../../directives/auto-size/auto-size.directive';
import { UiTableOrchestratorDirective } from '../../directives/table-orchestrator/table-orchestrator.directive';
import {
  PaginatorResponse,
  TableColumnOptions,
  TableFooterValues,
} from '../../models/table.models';
import { FeaturesMediaIfDirective } from '@brunsker/libs/core/features/media-if/src';

@Component({
  selector: 'bs-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [DETAIL_EXPANDED],
})
export class UiTableComponent<T> implements OnInit, AfterViewInit {
  @ViewChild(FeaturesMediaIfDirective, { static: true })
  readonly mediaIfDirective!: FeaturesMediaIfDirective<number>;
  @ViewChildren(MatPaginator) paginatorList!: QueryList<MatPaginator>;
  paginator!: MatPaginator;

  @ViewChild('autoSize', { static: true })
  table!: ElementRef<HTMLDivElement>;
  @ViewChild('tableScrollContainer')
  tableScrollContainer?: ElementRef<HTMLDivElement>;
  @ViewChild(UiTableOrchestratorDirective)
  tableOrchestratorInstance!: UiTableOrchestratorDirective<T>;

  @ViewChildren(UiAutoSizeDirective)
  readonly autoSizeDirective!: QueryList<UiAutoSizeDirective>;
  @ViewChildren('actionCellViewContainerRef', { read: ViewContainerRef })
  readonly actionQueryList!: QueryList<ViewContainerRef>;
  @ViewChildren('expandedDetailViewContainerRef', { read: ViewContainerRef })
  readonly expandedDetailQueryList!: QueryList<ViewContainerRef>;
  @HostBinding('class.has-sticky-header') @Input() floatHeader = false;
  @HostBinding('class.float-row') @Input() floatRow!: boolean;
  @Output() ngxPageChanged: EventEmitter<number> = new EventEmitter<number>();
  @Input() floatFooter!: boolean;
  @Input() currentPage!: number;
  @Input() itemsPerPage!: number;
  @Input() totalItems!: number;
  @Input() hasNgxPaginator!: boolean;
  @Input() matStyle!: boolean;
  @Input() hasAction!: boolean;
  @Input() hasPaginator: {
    staticPagination: boolean;
  } = {
    staticPagination: false,
  };
  @Input() hasPagination: boolean = false;
  @Input() hasServerSidePagination!: PaginatorResponse<any>;
  @Input() pageSize: number = 30;
  @Input() pageSizeOptions: number[] = [30, 50, 100, 200];
  @Output() bottomScrollEvent = new EventEmitter<void>();
  @Input() dataSource!: T[];
  @Input() columnsOptions!: TableColumnOptions[];
  @Input() footerValues?: TableFooterValues;
  @Input() elementKeys!: string[];
  @Input() expandedElementViewMedia!: number;
  @Input() automaticTableResizing!: number[];
  @Input() clickableRowFn?: (element: T) => void;
  @Output() eventAction = new EventEmitter<T>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Input() noImage!: string;
  columnsToDisplay!: string[];

  constructor(readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.columnsToDisplay = this.columnsOptions.map(
      (column) => column.columnName,
    );
  }

  ngAfterViewInit(): void {
    this.paginatorList.changes
      .pipe(startWith(this.paginatorList))
      .subscribe((paginatorList) => {
        this.paginator = paginatorList.last;
        this.changeDetectorRef.detectChanges();
      });

    const $table = this.tableScrollContainer?.nativeElement;

    if (!$table) {
      return;
    }

    fromEvent($table, 'scroll')
      .pipe(debounceTime(500))
      .subscribe(() => {
        const $tbody = $table.querySelector('tbody');

        if (
          $tbody &&
          $table.scrollTop + $table.clientHeight >= $tbody.scrollHeight - 500
        ) {
          this.bottomScrollEvent.emit();
        }
      });
  }
}
